import React from "react";
import { ArchMap } from "../archmap/ArchMap";
import { MapCard, MapLink } from "../archmap/types";

// Application architecture (clients, gateways, Internal API, ERP gateways, ERPs, Azure). Replaces the former
// mermaid graph of Arquitecture.md; the server details are in the on-premises map below.

const cards: MapCard[] = [
  {
    id: "clients",
    title: "Clients",
    description: "Apps and backends that call the gateways",
    accent: "#0277bd",
    nodes: [
      { id: "abfuhrkalender", name: "Abfuhrkalender", kind: "client", group: "via CentralGateway" },
      { id: "me", name: "Me", kind: "client", group: "via CentralGateway" },
      { id: "materialui", name: "MaterialUI", kind: "client", group: "via CentralGateway" },
      { id: "scadaplant", name: "ScadaPlant", kind: "client", group: "via CentralGateway" },
      { id: "handelsapp", name: "HandelsApp", kind: "client", group: "via CentralGateway" },
      { id: "sandbox", name: "Sandbox / Digido", kind: "client", group: "via CentralGateway" },
      { id: "kundenportal", name: "KundenPortal Backend", kind: "client", group: "via CentralGateway", note: "Also calls erpgwpr.genesiscloud.cc over the internet" },
      { id: "stgallen", name: "St. Gallen", kind: "client", group: "via ExternalGateway" },
      { id: "crequest", name: "CRequest", kind: "client", group: "via ExternalGateway" },
      { id: "telltexbackend", name: "TelltexBackend", kind: "client", group: "via ExternalGateway" },
      { id: "digido", name: "Digido", kind: "client", group: "via ExternalGateway" },
      { id: "mdm", name: "MDM", kind: "client", group: "Direct", note: "MasterData · Bulk · CO2 · RentriOrders" },
    ],
  },
  {
    id: "gateways",
    title: "Gateways",
    description: "Entry points with a Microsoft Entra token",
    accent: "#005f98",
    nodes: [
      { id: "centralgateway", name: "CentralGateway", kind: "proxy", note: "Azure App Service; health check in the blackbox job" },
      { id: "externalgateway", name: "ExternalGateway", kind: "proxy", note: "Lets users or applications with a valid Microsoft token operate actions in the micro services" },
    ],
  },
  {
    id: "innerapis",
    title: "GenesisCloud · Inner APIs",
    accent: "#00897b",
    nodes: [
      { id: "celldatasoap", name: "CellData Soap", kind: "app" },
      { id: "userapi", name: "User API", kind: "app" },
      { id: "newsapi", name: "NewsApi", kind: "app" },
      { id: "masterdataapi", name: "Master Data API", kind: "app" },
      { id: "materialdataapi", name: "MaterialData API", kind: "app" },
    ],
  },
  {
    id: "atgolorsdataapi",
    title: "Atgolorsdataapi",
    subtitle: "dev-003 · 10.192.193.3",
    description: "Genesis Internal API with its workers and scheduler jobs",
    accent: "#00897b",
    nodes: [
      { id: "internalapi", name: "Genesis Internal API", kind: "app", group: "Genesis Internal API", note: "Container genesis-internal-api-dev / -pr" },
      { id: "smstours", name: "SMS Tours Worker", kind: "job", group: "Workers" },
      { id: "rentriorders", name: "RentriOrders", kind: "job", group: "Workers", note: "Independent process" },
      { id: "co2", name: "Co2Automatic (CO2 Calc)", tag: "once", kind: "job", group: "Scheduler jobs", state: "disabled", note: "Enabled: false in appsettings" },
      { id: "janitzajob", name: "JanitzaCollection", tag: "daily", kind: "job", group: "Scheduler jobs", state: "disabled", note: "Equipment data points (Janitza worker). Enabled: false in appsettings" },
      { id: "rentrivalidation", name: "RentriValidation", tag: "Mon–Fri", kind: "job", group: "Scheduler jobs", state: "disabled", note: "Enabled: false in appsettings" },
      { id: "saphrsync", name: "SapHrSync", tag: "Mon–Sat", kind: "job", group: "Scheduler jobs", state: "disabled", note: "Enabled: false in appsettings" },
      { id: "erpgwlegacy", name: "ErpDataGateway (legacy)", kind: "app", group: "ErpDataGateway", note: "Containers erpdatagateway-prod / -re" },
    ],
  },
  {
    id: "outside",
    title: "External services",
    accent: "#757575",
    nodes: [
      { id: "rentriapi", name: "api.rentri.gov.it", kind: "external", mono: true },
      { id: "janitzaapi", name: "Janitza API", tag: "10.33.51.63:8080", kind: "external" },
      { id: "saphr", name: "SAP HR (OData)", kind: "external" },
      { id: "graph", name: "Microsoft Graph", kind: "external" },
    ],
  },
  {
    id: "entry",
    title: "Internet entry",
    accent: "#546e7a",
    nodes: [
      { id: "publicurl", name: "erpgwpr.genesiscloud.cc/graphql", kind: "proxy", mono: true, url: "https://erpgwpr.genesiscloud.cc/graphql" },
      { id: "firewall", name: "Firewall", kind: "security" },
    ],
  },
  {
    id: "rpx",
    title: "Reverse proxy",
    subtitle: "dev-rpx · 192.168.66.193",
    accent: "#005f98",
    nodes: [
      { id: "nginxrp", name: "nginx (Nginx Proxy Manager)", kind: "proxy", tag: "443" },
      { id: "erpgwpr", name: "erpgateway-pr (GraphQL)", kind: "app", tag: "5004" },
      { id: "erpgwre", name: "erpgateway-re (GraphQL)", kind: "app", tag: "5003" },
      { id: "balancer", name: "Balancer (round robin)", kind: "proxy", tag: "8080" },
    ],
  },
  {
    id: "erpgwservers",
    title: "ERP Gateway Servers",
    description: "Windows, migration to 10.192.193.1 / .2 planned",
    accent: "#e5ac58",
    nodes: [
      { id: "recygw1", name: "RecyGateway", tag: "4443", kind: "app", group: "Dev1 · 192.168.66.1" },
      { id: "ronagw1", name: "RonaGateway", tag: "3443", kind: "app", group: "Dev1 · 192.168.66.1" },
      { id: "telltexgw1", name: "TelltexGateway", tag: "2443", kind: "app", group: "Dev1 · 192.168.66.1" },
      { id: "recygw2", name: "RecyGateway", tag: "4443", kind: "app", group: "Dev2 · 192.168.66.2" },
      { id: "ronagw2", name: "RonaGateway", tag: "3443", kind: "app", group: "Dev2 · 192.168.66.2" },
      { id: "telltexgw2", name: "TelltexGateway", tag: "2443", kind: "app", group: "Dev2 · 192.168.66.2" },
    ],
  },
  {
    id: "erps",
    title: "Internal infrastructure · ERPs",
    accent: "#7b4fc9",
    nodes: [
      { id: "recydb", name: "RECY DB", kind: "db", group: "RECY · atgolossql01.genesis.root.loc" },
      { id: "recyonline", name: "Recy Online", kind: "app", group: "RECY · atgolossql01.genesis.root.loc" },
      { id: "recybp", name: "Recy BusinessProcess", kind: "app", group: "RECY · atgolossql01.genesis.root.loc" },
      { id: "recyimport", name: "RecyImport", kind: "app", group: "RECY · atgolossql01.genesis.root.loc" },
      { id: "ronadb", name: "RONA DB (loacker_prod)", kind: "db", group: "RONA · atgolos214.genesis.root.loc" },
      { id: "ronaapi", name: "Rona API (ContecMobile)", tag: ":48003", kind: "app", group: "RONA · atgolos214.genesis.root.loc", note: "rona.loacker.cc:48003/ContecMobile/?wsdl" },
      { id: "kendox", name: "Kendox documents", kind: "app", group: "RONA · atgolos214.genesis.root.loc", note: "ATGOLOS220/InfoShare" },
    ],
  },
  {
    id: "azure-data",
    title: "Azure · SAP import & storage",
    accent: "#0078d4",
    nodes: [
      { id: "sap", name: "SAP", kind: "external", group: "Source" },
      { id: "readsap", name: "Read Data from SAP", kind: "cloud", group: "Source" },
      { id: "st-users", name: "Users", kind: "db", group: "Storage" },
      { id: "st-accounts", name: "Accounts", kind: "db", group: "Storage" },
      { id: "st-production", name: "Production", kind: "db", group: "Storage" },
      { id: "st-masterdata", name: "Master Data", kind: "db", group: "Storage" },
      { id: "st-materialdata", name: "Material Data", kind: "db", group: "Storage" },
      { id: "st-communications", name: "Communications", kind: "db", group: "Storage" },
      { id: "st-redis", name: "Redis Cache", kind: "db", group: "Storage" },
    ],
  },
  {
    id: "azure-events",
    title: "Azure · queues & events",
    accent: "#0078d4",
    nodes: [
      { id: "q-update", name: "Queue UpdateInfo", kind: "queue" },
      { id: "eg-tours", name: "Event Grid Tours", kind: "queue" },
      { id: "q-sms", name: "Queue SMS", kind: "queue" },
      { id: "fn-sms", name: "Azure Functions SMS", kind: "cloud" },
      { id: "la-emails", name: "Send Emails Logic App", kind: "cloud" },
      { id: "fn-bulk", name: "Azure Functions Events-Bulk", kind: "cloud" },
      { id: "eg-master", name: "Event Grid MasterDataUpdates", kind: "queue" },
    ],
  },
  {
    id: "azure-celldata",
    title: "Azure · CellData",
    accent: "#0078d4",
    nodes: [
      { id: "celldata", name: "CellData", kind: "cloud" },
      { id: "celldataworker", name: "CellDataSamplingWorker", kind: "job" },
    ],
  },
];

const toCentral = ["abfuhrkalender", "me", "materialui", "scadaplant", "handelsapp", "sandbox", "kundenportal"];
const toExternal = ["stgallen", "crequest", "telltexbackend", "digido"];
const innerApis = ["celldatasoap", "userapi", "newsapi", "masterdataapi", "materialdataapi"];
const gateways = ["recygw1", "ronagw1", "telltexgw1", "recygw2", "ronagw2", "telltexgw2"];
const storage = ["st-users", "st-accounts", "st-production", "st-masterdata", "st-materialdata"];

const links: MapLink[] = [
  ...toCentral.map((id): MapLink => ({ from: id, to: "centralgateway", kind: "api" })),
  ...toExternal.map((id): MapLink => ({ from: id, to: "externalgateway", kind: "api" })),
  ...innerApis.map((id): MapLink => ({ from: id, to: "centralgateway", kind: "api" })),
  { from: "mdm", to: "internalapi", kind: "api" },
  { from: "centralgateway", to: "internalapi", kind: "api" },
  { from: "externalgateway", to: "internalapi", kind: "api" },

  // Genesis Internal API
  { from: "internalapi", to: "smstours", kind: "api" },
  { from: "internalapi", to: "rentriorders", kind: "api" },
  { from: "internalapi", to: "co2", kind: "api", label: "scheduler" },
  { from: "internalapi", to: "janitzajob", kind: "api", label: "scheduler" },
  { from: "internalapi", to: "rentrivalidation", kind: "api", label: "scheduler" },
  { from: "internalapi", to: "saphrsync", kind: "api", label: "scheduler" },
  { from: "internalapi", to: "erpgwpr", kind: "api", label: "GraphQL" },
  { from: "internalapi", to: "erpgwre", kind: "api", label: "GraphQL (dev config)" },
  { from: "internalapi", to: "recydb", kind: "data", label: "SQL RECY" },
  { from: "internalapi", to: "ronadb", kind: "data", label: "SQL loacker_prod" },
  { from: "internalapi", to: "graph", kind: "api" },
  ...storage.map((id): MapLink => ({ from: "internalapi", to: id, kind: "data", label: "Azure Storage" })),
  { from: "internalapi", to: "st-communications", kind: "data", label: "Azure Storage" },
  { from: "janitzajob", to: "janitzaapi", kind: "api" },
  { from: "rentriorders", to: "rentriapi", kind: "api" },
  { from: "rentrivalidation", to: "rentriapi", kind: "api", assumed: true },
  { from: "saphrsync", to: "saphr", kind: "api", label: "OData" },
  { from: "co2", to: "recydb", kind: "data", label: "Recy Prod" },
  { from: "erpgwlegacy", to: "recydb", kind: "data" },

  // Internet path to the ERP gateways
  { from: "kundenportal", to: "publicurl", kind: "proxy", label: "HTTPS" },
  { from: "publicurl", to: "firewall", kind: "proxy" },
  { from: "firewall", to: "nginxrp", kind: "proxy" },
  { from: "nginxrp", to: "erpgwpr", kind: "proxy" },
  { from: "nginxrp", to: "erpgwre", kind: "proxy" },
  { from: "erpgwpr", to: "balancer", kind: "proxy" },
  { from: "erpgwre", to: "balancer", kind: "proxy", assumed: true },
  ...gateways.map((id): MapLink => ({ from: "balancer", to: id, kind: "proxy", label: "round robin" })),
  { from: "recygw1", to: "recydb", kind: "data" },
  { from: "recygw2", to: "recydb", kind: "data" },
  { from: "ronagw1", to: "ronadb", kind: "data" },
  { from: "ronagw2", to: "ronadb", kind: "data" },

  // Azure
  { from: "sap", to: "readsap", kind: "data" },
  ...storage.map((id): MapLink => ({ from: "readsap", to: id, kind: "data" })),
  { from: "st-masterdata", to: "q-update", kind: "event", assumed: true },
  { from: "st-communications", to: "q-sms", kind: "event", assumed: true },
  { from: "q-update", to: "eg-tours", kind: "event" },
  { from: "q-sms", to: "fn-sms", kind: "event" },
  { from: "fn-sms", to: "la-emails", kind: "event" },
  { from: "fn-bulk", to: "eg-master", kind: "event" },
  { from: "celldata", to: "celldataworker", kind: "data" },
];

export const CloudMap: React.FunctionComponent = () => (
  <ArchMap
    title="Architektur"
    subtitle="Clients → gateways (Microsoft Entra token) → Genesis Internal API → ERP gateways and ERPs · Azure services below"
    stats={[
      { label: "Clients", value: cards[0].nodes.length },
      { label: "Jobs & workers", value: cards.find((c) => c.id === "atgolorsdataapi")!.nodes.filter((n) => n.kind === "job").length },
      { label: "ERP gateways", value: gateways.length },
    ]}
    cards={cards}
    columns={[["clients"], ["gateways", "innerapis"], ["atgolorsdataapi", "outside"], ["entry", "rpx", "erpgwservers"], ["erps"]]}
    gridTemplate="repeat(5, minmax(190px, 1fr))"
    bottom={["azure-data", "azure-events", "azure-celldata"]}
    bottomTitle="Azure Cloud"
    links={links}
  />
);
